import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Checkbox, Text } from 'components';
import { getDecodeCookie, setCookie } from 'common/Cookie';
import { useDispatch } from 'react-redux';
import EmailPasswordForm from './EmailPasswordForm';
import ThirdPartyLoginButtonSet from './ThirdPartyLoginButtonSet';
import { ThirdPartyLoginButtonProps } from './types';
import axios from 'axios';
import { login } from 'slices/loginSlice';
import _ from 'lodash';

interface Props {
  email: string;
  name: string;
  company: string;
  country: string;
  purpose: string;
  setName: Dispatch<SetStateAction<string>>;
  setCompany: Dispatch<SetStateAction<string>>;
  setEmail: Dispatch<SetStateAction<string>>;
  setCountry: Dispatch<SetStateAction<string>>;
  setPurpose: Dispatch<SetStateAction<string>>;
  setIsConfirming: Dispatch<SetStateAction<boolean>>;
}

interface PolicyTextProps {
  onRequiredCheck: (termsChecked: boolean, personalChecked: boolean) => void;
  setMarketingAgree: (value: boolean) => void;
  setPrivacyAgree: (value: boolean) => void;
  setTossAgree: (value: boolean) => void;
}

const openTermsOfUse = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();
  const width = 800;
  const height = 800;
  const left = window.screen.width / 2 - width / 2;
  const top = window.screen.height / 2 - height / 2;
  window.open(
    '/terms-of-service',
    `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`
  );
};


const PolicyText = ({ onRequiredCheck }: PolicyTextProps) => {
  const [termsofUse, setTermsofUse] = useState<boolean>(false);
  const [personalUse, setPersonalUse] = useState<boolean>(false);
  const [marketingAgreeState, setMarketingAgreeState] = useState<boolean>(false);
  const [privacyAgreeState, setPrivacyAgreeState] = useState<boolean>(false);
  const [tossAgreeState, setTossAgreeState] = useState<boolean>(false);

  useEffect(() => {
    onRequiredCheck(termsofUse, personalUse);
  }, [termsofUse, personalUse, onRequiredCheck]);

  /** 
  return (
    <div className="text-left">
      <Text size="small" color="primary" weight="regular">
      IOPS SPACE(주식회사 아이옵스)의 개인정보 수집 이용 목적은 다음과 같습니다.  
      내용을 자세히 읽어 보신 후 동의 여부를 결정하여 주시기 바랍니다.
      </Text>
      <div className='flex flex-row'>
        <Checkbox value={termsofUse} onChange={(value) => setTermsofUse(value)} inputKey="agree" />
        <Text className="flex" size="small" color="primary" weight="regular">
        (필수)<Link className="mx-1 inline-block text-system100" to="/terms-of-service" onClick={openTermsOfUse}>
          서비스 이용약관
          </Link>에 동의합니다.
        </Text>
      </div>
      <div className='flex flex-row'>
        <Checkbox value={personalUse} onChange={(value) => setPersonalUse(value)} inputKey="personalagree" />
        <Text className="flex" size="small" color="primary" weight="regular">
        (필수)<Link className="mx-1 inline-block text-system100" to="/privacy-policy">
          개인정보 수집 및 이용
          </Link>에 동의합니다.
        </Text>
      </div>
      <div className='flex flex-row'>
        <Checkbox value={marketingAgreeState} onChange={(value) => setMarketingAgreeState(value)} inputKey="marketingAgreeKey"/>
        <Text className="flex" size="small" color="primary" weight="regular">
        (선택) 마케팅 정보 수신 및 마케팅 목적으로 개인정보를 사용하는 데 동의합니다.
        </Text>
      </div>
      <Text className="flex" size="small" color="primary" weight="regular">
          - 마케팅 수신에 동의 : 수집된 정보는 주식회사 아이옵스가 서비스에 대한 정보, 팁 및 제안을 보내는 데 사용될 수 있습니다.
          <br/>
          - 수집하는 개인 정보 항목 : 이름, 이메일, 회사명, 거주 국가/지역 및 이 양식에 표시되는 기타 필드<br/>
          - 수집 및 사용 목적 : 마케팅 및 혜택에 관한 정보 제공<br/>
          - 선택 개인정보의 보유 기간 : 회원 탈퇴 혹은 마케팅 동의 해제 시 즉시파기
      </Text>
      <div className='flex flex-row'>
        <Checkbox value={privacyAgreeState} onChange={(value) => setPrivacyAgreeState(value)}  inputKey="privacyAgreeKey" />
        <Text className="flex" size="small" color="primary" weight="regular">
        귀하는 위와 같은 개인정보를 수집·이용하는데 동의를 거부할 권리가 있습니다. 필수 수집 항목에 대한 동의를 거절하는 경우        
        서비스 이용이 제한될 수 있으며 관련 모든 사항을 확인하였습니다.
        </Text>
      </div>
      <div className='flex flex-row'>
        <Checkbox value={tossAgreeState} onChange={(value) => setTossAgreeState(value)}  inputKey="tossAgreeKey" />
        <Text className="flex" size="small" color="primary" weight="regular">
        (선택) <Link className="mx-1 inline-block text-system100" to="/toss-of-use">제3자 정보 제공 동의</Link>를 확인하였으며 동의합니다.
        </Text>
      </div>
    </div>
  );
};
*/

  return (
    <div className="text-left space-y-3">
      <Text size="small" color="primary" weight="regular">
        The purpose of collecting and using personal information by IOPS SPACE is as follows. Please read the details carefully and decide whether to agree.
      </Text>

      <div className="flex items-start gap-x-2">
        <Checkbox value={termsofUse} onChange={setTermsofUse} inputKey="agree" />
        <Text size="small" color="primary" weight="regular">
          (Required) I agree to the{' '}
          <Link className="text-system100" to="/terms-of-service" target="_blank">
            Terms of Service
          </Link>.
        </Text>
      </div>

      <div className="flex items-start gap-x-2">
        <Checkbox value={personalUse} onChange={setPersonalUse} inputKey="personalagree" />
        <Text size="small" color="primary" weight="regular">
          (Required) I agree to the{' '}
          <Link className="text-system100" to="/privacy-policy" target="_blank">
            Privacy Policy
          </Link>.
        </Text>
      </div>

      <div className="flex items-start gap-x-2">
        <Checkbox value={marketingAgreeState} onChange={setMarketingAgreeState} inputKey="marketingAgreeKey" />
        <Text size="small" color="primary" weight="regular">
          (Optional) I agree to receive marketing communications and to the use of my personal information for marketing purposes.
        </Text>
      </div>

      <Text size="small" color="primary" weight="regular">
        - Agreement to marketing communications: Your information may be used by IOPS Co., Ltd. to send you updates, tips, and promotional offers.<br />
        - Information collected: Name, Email, Company Name, Country/Region of Residence, and other fields in this form.<br />
        - Purpose of collection and use: To provide information on marketing and benefits.<br />
        - Retention period for optional personal information: Immediately discarded upon withdrawal of consent or account deletion.
      </Text>

      <div className="flex items-start gap-x-2">
        <Checkbox value={privacyAgreeState} onChange={setPrivacyAgreeState} inputKey="privacyAgreeKey" />
        <Text size="small" color="primary" weight="regular">
          You have the right to refuse the collection and use of the above personal information. Please note that refusal to agree to the required items may limit your ability to use the service. I have reviewed and understood all related information.
        </Text>
      </div>

      <div className="flex items-start gap-x-2">
        <Checkbox value={tossAgreeState} onChange={setTossAgreeState} inputKey="tossAgreeKey" />
        <Text size="small" color="primary" weight="regular">
          (Optional) I have read and agree to the{' '}
          <Link className="text-system100" to="/toss-of-use" target="_blank">
            Third-Party Information Provision Agreement
          </Link>.
        </Text>
      </div>
    </div>
  );
};

const emailRegexp = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g;
const passwordRegexp = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;

const SignUpForm = ({ email, name, company, country, purpose, setEmail, setIsConfirming, setName, setCompany, setCountry, setPurpose }: Props) => {
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [countryValue, setCountryValue] = useState<any>('');
  const [isEmailFormShown, setIsEmailFormShown] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [nameError, setNameError] = useState('');
  const [companyError, setCompanyError] = useState('');
  const [countryError, setCountryError] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [googleAuthMode, setGoogleAuthMode] = useState<'signup' | 'convert'>('signup');
  const [termsChecked, setTermsChecked] = useState(false);
  const [personalChecked, setPersonalChecked] = useState(false);
  const [marketingAgree, setMarketingAgree] = useState(false);
  const [privacyAgree, setPrivacyAgree] = useState(false);
  const [tossAgree, setTossAgree] = useState(false);
  const [termsError, setTermsError] = useState('');
  const nav = useNavigate();
  const dispatch = useDispatch();

  const onEmailBlur = () => {
    if (!email) {
      setEmailError('Email is required.');
    } else if (!emailRegexp.test(email)) {
      setEmailError('Invalid email address.');
    } else {
      setEmailError('');
    }
  };

  const onPasswordBlur = () => {
    if (!password) {
      setPasswordError('Password is required.');
    } else if (!passwordRegexp.test(password)) {
      setPasswordError('Password must be at least 8 characters and include letters, numbers, and special characters.');
    } else {
      setPasswordError('');
    }
  };

  const onNameBlur = () => {
    if (!firstName || !lastName) {
      setNameError('Full name is required.');
    } else {
      setNameError('');
    }
    setName(`${firstName} ${lastName}`);
  };


  const onCompanyBlur = () => {
    if (!company) {
      setCompanyError('Company is required.');
    } else {
      setCompanyError('');
    }
  };

  const onCountryBlur = () => {
    if (!countryValue || !countryValue.label) {
      setCountryError('Country is required.');
    } else {
      setCountryError('');
      setCountry(countryValue.label);
    }
  };

  useEffect(() => {
    const isValidInput =
      email &&
      !emailError &&
      !passwordError &&
      !nameError &&
      !companyError &&
      password.length >= 8 &&
      termsChecked &&
      personalChecked &&
      countryValue?.label;

    setIsButtonDisabled(!isValidInput);
  }, [
    email, emailError,
    password, passwordError,
    nameError, companyError,
    countryValue,
    termsChecked, personalChecked
  ]);

  const [countryTouched, setCountryTouched] = useState(false);

  const onCountryChange = (value: any) => {
    setCountryValue(value);
    setCountryTouched(true);
  };

  useEffect(() => {
    if (!countryTouched) return;

    if (!countryValue || !countryValue.label) {
      setCountryError('Country is required.');
    } else {
      setCountryError('');
      setCountry(countryValue.label);
    }
  }, [countryValue, countryTouched]);


  const handleRequiredCheck = (terms: boolean, personal: boolean) => {
    setTermsChecked(terms);
    setPersonalChecked(personal);
  };

  const onClickEmailButton = async () => {
    if (!isEmailFormShown) {
      setIsEmailFormShown(true);
      return;
    }
    if (!termsChecked || !personalChecked) {
      setTermsError('You must agree to the required terms.');
      return;
    } else {
      setTermsError('');
    }
    if (isButtonDisabled) return;
    try {
      const res = await axios.post('/api/member/register', {
        email, name, company, country, password, purpose,
        marketingAgree, privacyAgree, tossAgree,
      });
      setIsConfirming(true);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data.method === 'EXIST_EMAIL') {
          setEmailError('Email is already in use.');
        } else {
          alert('회원가입 실패: 서버 오류가 발생했습니다.');
        }
      } else {
        alert('네트워크 오류가 발생했습니다.');
      }
    }
  };

  const signinButtonCallbackObj: ThirdPartyLoginButtonProps = {
    loginType: 'Sign Up',
    onClickGoogleButton: async (code: string, redirectUri: string) => {
      try {
        const endpoint = googleAuthMode === 'convert' ? '/api/member/auth/google/convert' : '/api/member/auth/google';
        const response = await axios.post(endpoint, { code, redirectUri });

        if (response.data.code === 'SUCCESS') {
          const { accessToken } = response.data.data;
          setCookie('accessToken', accessToken);
          const token = getDecodeCookie('accessToken');
          if (token != null) {
            const payload = _.cloneDeep(token);
            nav('/get-started');
            dispatch(login(payload));
          }
        }
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const errorData = err.response?.data;
          if (errorData?.method === 'EXIST_EMAIL') {
            const confirmConvert = window.confirm('This email address is already registered. Would you like to switch to a Google account?');
            if (confirmConvert) setGoogleAuthMode('convert');
            else nav('/login');
          } else nav('/login');
        }
      }
    },
  };


  return (
    <div className="flex w-80 flex-col items-center">
      <Text className="mb-6" size="heading2" weight="medium">
        Sign Up
      </Text>

      {/* 회원가입 화면 */}
      {isEmailFormShown ? (
        <>
          <EmailPasswordForm
            email={email}
            password={password}
            company={company}
            purpose={purpose}
            firstName={firstName}
            lastName={lastName}
            country={countryValue}
            setEmail={setEmail}
            setPassword={setPassword}
            setFirstName={setFirstName}
            setLastName={setLastName}
            setCompany={setCompany}
            setPurpose={setPurpose}
            setCountry={setCountryValue}
            onEmailBlur={onEmailBlur}
            onPasswordBlur={onPasswordBlur}
            onNameBlur={onNameBlur}
            onCompanyBlur={onCompanyBlur}
            onCountryBlur={onCountryBlur}
            emailError={emailError}
            passwordError={passwordError}
            nameError={nameError}
            companyError={companyError}
            countryError={countryError}
          />
          <PolicyText
            onRequiredCheck={handleRequiredCheck}
            setMarketingAgree={setMarketingAgree}
            setPrivacyAgree={setPrivacyAgree}
            setTossAgree={setTossAgree}
          />
          {termsError && (
            <Text className="mt-2 !text-red-500" size="body2" weight="regular">
              {termsError}
            </Text>
          )}
        </>
      ) : (
        <ThirdPartyLoginButtonSet {...signinButtonCallbackObj} />
      )}
      {/* 이메일 가입 전환 버튼 */}
      <div className="mt-2 flex w-full flex-col gap-6">
        <Button
          className="h-10 w-full items-center justify-center"
          text={isEmailFormShown ? 'Create Account' : 'Sign Up with Email'}
          type="blue"
          aria-label="sign up submit"
          disabled={isEmailFormShown ? isButtonDisabled : false}
          onClick={onClickEmailButton}
        />
      </div>
    </div>
  );
};

export default SignUpForm;